import { IconCharter16 } from '@warp-ds/icons/react';
import { Button, Card, Clickable } from '@warp-ds/react';
import { ImageCarousel } from 'shared/components/ImageCarousel/ImageCarousel';
import { useConfig } from 'shared/config/ConfigContext';
import { thousandSeparated } from 'shared/util/formatting';
import { useUnleash } from '@/hooks/unleash';
import type { Filters, Offer } from '@/interfaces';
import { getNumberOfPax, getPricePerPerson, getPriceTotal } from '@/lib/utils/hotelData';
import { addHotelPageFiltersToUrl, browserQueryParamFor } from '@/lib/utils/queryParamFilterConversions';
import { trackSeeMoreOffers, trackTicketOfferClick } from '@/lib/utils/tracking';
import { SupplierLogo } from '../images/SupplierLogo';
import { StarRating } from './StarRating';

export interface ResultListItemProps {
    offer: Offer;
    filters: Filters;
    variant: string | undefined;
}

const meals: { [index: string]: string } = {
    all_inclusive: 'All inclusive',
    breakfast: 'Frokost',
    half_pension: 'Halvpensjon',
    full_pension: 'Fullpensjon',
};

export const ResultListItemClickable = ({ offer, filters, variant }: ResultListItemProps) => {
    const { contextPathPP, imgPath } = useConfig();
    const unleash = useUnleash();

    const numPax = getNumberOfPax(offer.numberOfAdults, offer.numberOfChildren);
    const pricePerPerson = thousandSeparated(getPricePerPerson(offer.pricePerPerson, numPax, offer.priceTotal));
    const priceTotal = thousandSeparated(getPriceTotal(offer.pricePerPerson, numPax, offer.priceTotal));

    const hotelUrl = addHotelPageFiltersToUrl(`${contextPathPP}/hotell?${browserQueryParamFor('hotelId', offer.hotelId)}`, filters);

    const joinLocation = (...parts: Array<string | null | undefined>) =>
        parts
            .map((s) => s?.trim())
            .filter((s): s is string => !!s && s.length > 0)
            .join(', ');

    const getFormattedTravelDates = (dateStringDeparture: string, dateStringReturn: string) => {
        const norwegianMonths = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
        const departureDate = new Date(dateStringDeparture);
        const returnDate = new Date(dateStringReturn);
        const departureMonth = norwegianMonths[departureDate.getMonth()];
        const returnMonth = norwegianMonths[returnDate.getMonth()];

        if (departureMonth === returnMonth) {
            return `${departureDate.getDate()}.–${returnDate.getDate()}. ${departureMonth}`;
        } else {
            return `${departureDate.getDate()}. ${departureMonth} – ${returnDate.getDate()}. ${returnMonth}`;
        }
    };

    const images = offer.imagePaths.map((path) => `${imgPath}480w${path}`);

    return (
        <Card className="!cursor-default !transition-none !active:s-surface-elevated-200 !hover:s-surface-elevated-200">
            <article
                className="flex flex-col border s-border rounded-8 md:flex-row md:min-h-[186px]"
                data-testid={`resultListItem-${offer.offerId}`}
                data-result-item
                aria-owns={`resultListItem-${offer.offerId}`}
                tabIndex={-1}>
                {/** biome-ignore lint/a11y/noStaticElementInteractions: Suppress clicks reaching parent Card component */}
                <div
                    className="md:w-1/2 w-full md:max-w-[400px] md:max-h-[220px] overflow-hidden rounded-tl-8 rounded-tr-8 md:rounded-tr-0 md:rounded-bl-8"
                    onClick={(e) => e.stopPropagation()}>
                    {unleash?.imageCarousel ? (
                        <ImageCarousel images={images} alt={offer.hotelName ?? 'Hotellbilde'} className="h-full" />
                    ) : (
                        <img src={images[0]} alt={offer.hotelName ?? 'Hotellbilde'} className="w-full h-full object-cover" />
                    )}
                </div>

                <div className="flex flex-col md:flex-row w-full justify-between">
                    <header className="flex flex-col gap-16 p-16">
                        <div className="flex flex-row justify-between items-center">
                            <StarRating id={`rating-${offer.offerId}`} rating={offer.rating} emptyStars={true} />
                            <SupplierLogo supplier={offer.supplier} className="md:hidden" />
                        </div>

                        <div>
                            <h2 className="t4 mb-4" data-testid="offerHotelName" id={`resultListItem-${offer.offerId}`}>
                                <Clickable
                                    href={hotelUrl}
                                    onClick={() => {
                                        if (variant && offer.supplier === 'ticket') {
                                            trackTicketOfferClick(offer.offerId, offer.hotelName, 'Open');
                                        }
                                        trackSeeMoreOffers(offer.offerId, offer.hotelName);
                                    }}
                                    className="no-underline hover:no-underline s-text">
                                    {offer.hotelName}
                                </Clickable>
                            </h2>
                            <p className="s-text-subtle text-xs mb-0" data-testid="offerDestination">
                                {joinLocation(offer.destination, offer.region, offer.country)}
                            </p>
                        </div>
                        <p className="text-caption font-normal flex">
                            <IconCharter16 className="pr-4" />{' '}
                            {` ${getFormattedTravelDates(offer.departureDate, offer.returnDate)} • ${offer.duration} netter`}
                            {offer.board && offer.board !== 'no_meals' && (
                                <span className="pl-4" data-testid="offerMeal">
                                    {` • ${meals[offer.board]}`}
                                </span>
                            )}
                        </p>
                    </header>

                    <footer className="flex flex-col p-16 justify-between md:min-w-[144px] border-t md:border-t-[0] md:border-l s-border md:items-end">
                        <SupplierLogo supplier={offer.supplier} className="hidden md:block" />

                        <div className="flex flex-col gap-16">
                            <div className="flex flex-row md:flex-col items-baseline md:items-end gap-8 mb:gap-4">
                                <p className="t3 m-0">{`${pricePerPerson} kr`}</p>
                                <p className="text-detail m-0">{`${priceTotal} kr/${numPax} personer`}</p>
                            </div>
                            <Button
                                primary
                                fullWidth
                                href={hotelUrl}
                                onClick={() => {
                                    if (variant && offer.supplier === 'ticket') {
                                        trackTicketOfferClick(offer.offerId, offer.hotelName, 'Open');
                                    }
                                    trackSeeMoreOffers(offer.offerId, offer.hotelName);
                                }}
                                rel="nofollow"
                                data-testid={`resultDetailLink-${offer.hotelId}`}
                                className="hidden md:block">
                                Se tilbud
                                <span className="sr-only"> for {offer.hotelName}</span>
                            </Button>
                        </div>
                    </footer>
                </div>
            </article>
        </Card>
    );
};
